"use client";

import { motion, AnimatePresence } from "framer-motion";
import {
  Mic,
  Music as MusicIcon,
  FileAudio,
  Sparkles,
  Info,
} from "lucide-react";
import { useState } from "react";

export type AnalysisType = "vocal" | "instrument" | "composition";

export interface AnalysisTypeDetection {
  type: AnalysisType;
  confidence: number;
}

interface AnalysisTypeSelectorProps {
  detectedType?: AnalysisTypeDetection;
  selectedType: AnalysisType;
  onTypeChange: (type: AnalysisType) => void;
  isLoading?: boolean;
}

const analysisTypes = [
  {
    id: "vocal" as AnalysisType,
    label: "보컬",
    icon: Mic,
    color: "from-purple-600 to-pink-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-300",
    textColor: "text-purple-700",
    description: "음정 정확도, 리듬 안정성, 감정 표현을 평가합니다.",
  },
  {
    id: "instrument" as AnalysisType,
    label: "연주",
    icon: MusicIcon,
    color: "from-blue-600 to-cyan-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-300",
    textColor: "text-blue-700",
    description: "리듬, 톤, 에너지 밸런스를 중심으로 분석합니다.",
  },
  {
    id: "composition" as AnalysisType,
    label: "작곡",
    icon: FileAudio,
    color: "from-indigo-600 to-purple-600",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-300",
    textColor: "text-indigo-700",
    description: "구조, 감정 흐름, 장르 적합성을 분석합니다.",
  },
];

export default function AnalysisTypeSelector({
  detectedType,
  selectedType,
  onTypeChange,
  isLoading,
}: AnalysisTypeSelectorProps) {
  const [showDescription, setShowDescription] = useState<string | null>(null);
  const [isManualMode, setIsManualMode] = useState(false);

  const selectedTypeData = analysisTypes.find(t => t.id === selectedType);

  return (
    <div className="space-y-6">
      {/* AI 자동 감지 결과 */}
      {detectedType && !isManualMode && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2">
                AI가 자동으로 분석 유형을 감지했습니다
              </h3>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  {selectedTypeData && (
                    <>
                      <selectedTypeData.icon className="w-5 h-5 text-purple-600" />
                      <span className="text-lg font-semibold text-purple-700">
                        {selectedTypeData.label}
                      </span>
                    </>
                  )}
                </div>
                <div className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-600">
                  신뢰도: {Math.round(detectedType.confidence * 100)}%
                </div>
              </div>
              <button
                onClick={() => setIsManualMode(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 via-fuchsia-500 to-indigo-500 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl hover:brightness-105"
              >
                다른 유형 선택하기
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* 수동 선택 모드 또는 감지 실패 */}
      {(isManualMode || !detectedType) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* 안내 문구 */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Info className="w-4 h-4" />
            <span>
              {detectedType
                ? "AI가 자동으로 분석했지만, 직접 변경할 수도 있어요"
                : "AI가 유형을 인식하지 못했습니다. 직접 선택해주세요."}
            </span>
          </div>

          {/* PC: Segmented Control */}
          <div className="hidden md:grid md:grid-cols-3 gap-4">
            {analysisTypes.map(type => {
              const Icon = type.icon;
              const isSelected = selectedType === type.id;

              return (
                <button
                  key={type.id}
                  onClick={() => onTypeChange(type.id)}
                  onMouseEnter={() => setShowDescription(type.id)}
                  onMouseLeave={() => setShowDescription(null)}
                  disabled={isLoading}
                  className={`
                    relative p-6 rounded-xl border-2 transition-all duration-300
                    ${
                      isSelected
                        ? `${type.borderColor} ${type.bgColor} shadow-lg scale-105`
                        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-md"
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className={`
                      w-16 h-16 rounded-full flex items-center justify-center
                      ${
                        isSelected
                          ? `bg-gradient-to-br ${type.color}`
                          : "bg-gray-100"
                      }
                    `}
                    >
                      <Icon
                        className={`w-8 h-8 ${
                          isSelected ? "text-white" : "text-gray-600"
                        }`}
                      />
                    </div>
                    <div>
                      <div
                        className={`text-lg font-bold ${
                          isSelected ? type.textColor : "text-gray-900"
                        }`}
                      >
                        {type.label}
                      </div>
                    </div>
                  </div>

                  {/* 선택 표시 */}
                  {isSelected && (
                    <motion.div
                      layoutId="selected-indicator"
                      className={`absolute inset-0 border-2 ${type.borderColor} rounded-xl`}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* 모바일: 드롭다운 형태 */}
          <div className="md:hidden">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              분석 유형 선택
            </label>
            <select
              value={selectedType}
              onChange={e => onTypeChange(e.target.value as AnalysisType)}
              disabled={isLoading}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
            >
              {analysisTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* 선택된 유형 설명 (PC) */}
          <AnimatePresence>
            {showDescription && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                {analysisTypes.map(
                  type =>
                    showDescription === type.id && (
                      <div
                        key={type.id}
                        className={`p-4 ${type.bgColor} rounded-xl border ${type.borderColor}`}
                      >
                        <p className={`text-sm ${type.textColor}`}>
                          {type.description}
                        </p>
                      </div>
                    )
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* 선택된 유형 설명 (모바일 - 항상 표시) */}
          <div className="md:hidden">
            {selectedTypeData && (
              <div
                className={`p-4 ${selectedTypeData.bgColor} rounded-xl border ${selectedTypeData.borderColor}`}
              >
                <p className={`text-sm ${selectedTypeData.textColor}`}>
                  {selectedTypeData.description}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
